/**
 * Singleton (per-game, registry-backed) that coordinates the hover/click
 * profile tooltip. Any Variant-B surface that wants to expose an author
 * profile resolves this service from the registry and calls `show(...)`.
 *
 * Two trigger modes:
 *
 *   - Hover surfaces (shop card, author entry banner, future map row) call
 *     `show(seed, { trigger: "hover" })` on pointerover. After 200 ms the
 *     scene actually appears, so accidental swipes don't flicker. They
 *     call `hide("mouse_leave")` on pointerout / when the element goes away.
 *
 *   - The runner HUD AuthorBadge is click-only. It calls
 *     `show(seed, { trigger: "click", immediate: true, modalForSceneKey: "runner" })`
 *     which pauses the runner scene under the tooltip and resumes on dismiss.
 *
 * See `docs/superpowers/specs/2026-05-18-profile-hover-tooltip-design.md`.
 */

import Phaser from "phaser";

import { t } from "../i18n";

import type { AuthorIdentityService } from "./AuthorIdentityService";
import { ProfileTooltipScene, type ProfileAnchor, type ProfileDismissReason } from "../scenes/ProfileTooltipScene";

type TelemetryEmit = (
  layer: 1 | 2 | 3,
  tag: string,
  data?: Record<string, number | string | boolean>,
) => void;

export type ProfileSource = "shop" | "banner" | "hud" | "map";
type ProfileTrigger = "hover" | "click";

export interface ProfileShowOpts {
  source: ProfileSource;
  trigger: ProfileTrigger;
  anchor: ProfileAnchor;
  /** When set, the named scene is paused while the tooltip is visible. */
  modalForSceneKey?: string;
  /** Skip the 200 ms hover delay. Default true for `click`, false for `hover`. */
  immediate?: boolean;
}

interface CreateArgs {
  game: Phaser.Game;
  identity: AuthorIdentityService;
  emitTelemetry: TelemetryEmit;
}

const HOVER_DELAY_MS = 200;
const REGISTRY_KEY = "profileTooltipService";

export class ProfileTooltipService {
  private readonly game: Phaser.Game;
  private readonly identity: AuthorIdentityService;
  private readonly emitTelemetry: TelemetryEmit;

  /** Seed of the currently-shown profile (or the one pending its delay). */
  private currentSeed?: number;
  private currentSource?: ProfileSource;
  private currentUsername?: string;
  private shownAtMs = 0;
  private pendingTimer?: Phaser.Time.TimerEvent;

  static getOrCreate(args: CreateArgs): ProfileTooltipService {
    const existing = args.game.registry.get(REGISTRY_KEY) as ProfileTooltipService | undefined;
    if (existing) return existing;
    const svc = new ProfileTooltipService(args);
    args.game.registry.set(REGISTRY_KEY, svc);
    return svc;
  }

  static get(game: Phaser.Game): ProfileTooltipService | undefined {
    return game.registry.get(REGISTRY_KEY) as ProfileTooltipService | undefined;
  }

  private constructor(args: CreateArgs) {
    this.game = args.game;
    this.identity = args.identity;
    this.emitTelemetry = args.emitTelemetry;
    args.game.registry.set("authorIdentityService", args.identity);
  }

  show(seed: number, opts: ProfileShowOpts): void {
    const immediate = opts.immediate ?? opts.trigger === "click";

    // Re-arming on the same seed: ignore (prevents flicker on jitter).
    if (this.currentSeed === seed && (this.isVisible() || this.pendingTimer)) {
      return;
    }
    // Different surface or seed → cancel any pending and any currently-shown.
    this.cancelPending();
    if (this.isVisible()) {
      this.hide("toggle");
    }

    this.currentSeed = seed;
    this.currentSource = opts.source;

    if (immediate) {
      this.actuallyShow(seed, opts);
      return;
    }

    const scene = this.getOverlayScene();
    if (!scene) return;
    this.pendingTimer = scene.time.delayedCall(HOVER_DELAY_MS, () => {
      this.pendingTimer = undefined;
      // Re-check: another show() / cancelPending() may have replaced state
      // while we waited.
      if (this.currentSeed !== seed) return;
      this.actuallyShow(seed, opts);
    });
  }

  /** Cancel a pending hover-delay timer (called from pointerout before
   *  the tooltip actually appears). */
  cancelPending(): void {
    if (this.pendingTimer) {
      this.pendingTimer.remove(false);
      this.pendingTimer = undefined;
    }
    // If we're cancelling before show actually happened, clear the seed too
    // so subsequent shows on the same seed aren't suppressed.
    if (!this.isVisible()) {
      this.currentSeed = undefined;
      this.currentSource = undefined;
    }
  }

  hide(reason: ProfileDismissReason): void {
    this.cancelPending();
    const scene = this.getOverlayScene();
    if (!scene) return;
    if (!scene.isVisible()) return;
    scene.hide(reason);
    // Telemetry is emitted by the onDismiss callback wired in actuallyShow.
  }

  isVisible(): boolean {
    return this.getOverlayScene()?.isVisible() ?? false;
  }

  private actuallyShow(seed: number, opts: ProfileShowOpts): void {
    const scene = this.getOverlayScene();
    if (!scene) return;
    const profile = this.identity.profileForSeed(seed);
    this.currentUsername = profile.username;
    this.shownAtMs = this.game.loop.now;

    scene.show({
      profile,
      anchor: opts.anchor,
      modalForSceneKey: opts.modalForSceneKey,
      strings: {
        stats: t("profile.stats", {
          levels: profile.levelsAuthored,
          trades: profile.tradesCompleted,
        }),
        memberSince: t("profile.member_since", {
          month: profile.memberSinceMonth.toString().padStart(2, "0"),
          year: profile.memberSinceYear.toString(),
        }),
      },
      identityService: this.identity,
      onDismiss: (reason) => this.onSceneDismiss(reason),
    });

    this.emitTelemetry(1, "ugc.author.profile.shown", {
      source: opts.source,
      trigger: opts.trigger,
      authorUsername: profile.username,
      authorSeed: seed,
    });
  }

  private onSceneDismiss(reason: ProfileDismissReason): void {
    const dwellMs = this.shownAtMs > 0 ? Math.round(this.game.loop.now - this.shownAtMs) : 0;
    this.emitTelemetry(1, "ugc.author.profile.dismissed", {
      source: this.currentSource ?? "shop",
      authorUsername: this.currentUsername ?? "",
      dwellMs,
      dismissReason: reason,
    });
    this.currentSeed = undefined;
    this.currentSource = undefined;
    this.currentUsername = undefined;
    this.shownAtMs = 0;
  }

  private getOverlayScene(): ProfileTooltipScene | undefined {
    const s = this.game.scene.getScene(ProfileTooltipScene.KEY);
    return s instanceof ProfileTooltipScene ? s : undefined;
  }
}
